import { useCallback, useMemo } from 'react';
import {
  Grid,
  TypographyWithHelp,
  WidgetContainer,
  S6,
  useTranslations,
  useSnackbar,
  Typography,
  S4,
  List,
  getDatesDiff,
} from '@uniqkey-frontend/shared-app';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { ApplicationOrderPropertyName } from '@uniqkey-backend-organization-web/api-client';
import { lowerFirst } from 'lodash';
import { useGetServices } from '../../../../../../hooks/reactQuery';
import PageRouteEnum from '../../../../../../enums/PageRouteEnum';
import DashboardUnusedServicesWidgetListItem from
  './components/DashboardUnusedServicesWidgetListItem';
import ReactQueryKeyEnum from '../../../../../../enums/ReactQueryKeyEnum';
import DashboardServicesNoDataWidget from '../DashboardServicesNoDataWidget';

const DashboardUnusedServicesWidget = () => {
  const { t } = useTranslations();
  const { showError } = useSnackbar();
  const navigate = useNavigate();

  const { data, isLoading } = useGetServices({
    page: 1,
    pageLength: 5,
    isDescending: false,
    orderPropertyName: ApplicationOrderPropertyName.ActivityAt,
  }, {
    queryKey: [ReactQueryKeyEnum.DashboardUnusedServices],
    onError: () => {
      showError({ text: t('common.somethingWentWrong') });
    },
  });

  const handleNavigation = useCallback(() => {
    const search = createSearchParams({
      columnOrderBy: lowerFirst(ApplicationOrderPropertyName.ActivityAt),
      columnOrderDirection: 'asc',
    }).toString();
    navigate({
      pathname: PageRouteEnum.Services,
      search,
    });
  }, [navigate]);

  const { data: preServices } = data ?? {};

  const services = useMemo(() => {
    if (!preServices) {
      return [];
    }
    const dateNow = new Date();
    return preServices.filter((service) => {
      if (!service.activityAt) {
        return false;
      }
      const dateFrom = new Date(service.activityAt);
      const { days } = getDatesDiff({ from: dateFrom, to: dateNow });
      return days > 90;
    });
  }, [preServices]);

  if (isLoading) {
    return (
      <WidgetContainer container withShadow isLoading={isLoading} minHeight={270} />
    );
  }

  if (!services.length) {
    return (
      <DashboardServicesNoDataWidget
        tooltipTranslationKey="dashboardUnusedServicesWidget.tooltip"
        titleTranslationKey="dashboardUnusedServicesWidget.title"
        noDataTranslationKey="dashboardUnusedServicesWidget.noData"
      />
    );
  }

  return (
    <WidgetContainer
      container
      withShadow
      p={0}
    >
      <Grid container py={2} rowGap={1}>
        <Grid item xs={12} px={3}>
          <Grid item>
            <TypographyWithHelp
              helperText={t('dashboardUnusedServicesWidget.tooltip')}
              color={S6}
            >
              {t('dashboardUnusedServicesWidget.title')}
            </TypographyWithHelp>
          </Grid>
          <Grid item container justifyContent="space-between">
            <Grid item>
              <Typography variant="caption" color={S4}>
                {t('dashboardUnusedServicesWidget.services')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color={S4}>
                {t('dashboardUnusedServicesWidget.lastActivity')}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <List disablePadding>
            {services.map((service, index) => (
              <DashboardUnusedServicesWidgetListItem
                service={service}
                index={index}
                key={service.applicationId}
              />
            ))}
          </List>
        </Grid>
        <Grid item my={2} container justifyContent="center">
          <Typography asLink onClick={handleNavigation}>
            {t('dashboardUnusedServicesWidget.viewAllServicesButton')}
          </Typography>
        </Grid>
      </Grid>
    </WidgetContainer>
  );
};

export default DashboardUnusedServicesWidget;
